import { useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Coins, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { buildCheckoutUrl } from "@/lib/checkoutUrl";
import PaymentTrust from "@/components/PaymentTrust";

// Highlight the middle-tier pack as "Most popular" for social proof.
const POPULAR_INDEX = 3;

export default function BuyCredits() {
  const { siteSettings, user } = useAuth();
  const packs = useMemo(
    () => (Array.isArray(siteSettings?.credit_packages) ? siteSettings.credit_packages : []),
    [siteSettings]
  );

  const buy = (pack) => {
    const url = buildCheckoutUrl(pack.url, user, {
      pack_id: pack.id,
      credits: pack.credits,
      plan: pack.name,
    });
    if (!url) {
      toast.info(`${pack.name} pack isn't live yet`, {
        description: "The admin hasn't set the checkout link for this pack.",
      });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const per1000 = (p) => (p.price_usd / p.credits * 1000);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400">Store</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Buy Credits
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Fuel your traffic. Bigger packs = better cost per 1,000 credits.
          </p>
        </div>

        {packs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400">
            Credit packs are being prepared. Please check back shortly.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packs.map((p, i) => {
            const popular = i === POPULAR_INDEX;
            return (
              <div
                key={p.id || `pack-${i}`}
                data-testid={`credits-pack-${p.id || i}`}
                className={`relative rounded-2xl border p-5 flex flex-col transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg ${
                  popular
                    ? "border-lime-400 bg-gradient-to-br from-white to-lime-50 dark:from-slate-900 dark:to-slate-900 ring-2 ring-lime-400/60"
                    : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                }`}
              >
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-lime-400 text-slate-900 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 shadow">
                    <Sparkle size={10} weight="fill" /> Most popular
                  </span>
                )}
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <Coins size={20} weight="fill" className="text-amber-500" />
                  <span className="text-xs font-black uppercase tracking-widest">{p.name}</span>
                </div>
                <div className="mt-3 text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                  {Number(p.credits).toLocaleString()}
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">credits</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-slate-900 dark:text-white">${Number(p.price_usd).toFixed(2)}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">USD</span>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  ${per1000(p).toFixed(2)} / 1,000 credits
                </div>
                <button
                  data-testid={`credits-buy-${p.id || i}`}
                  onClick={() => buy(p)}
                  className={`mt-5 inline-flex items-center justify-center gap-1 rounded-full px-4 py-2.5 text-sm font-black uppercase tracking-widest transition ${
                    popular
                      ? "bg-lime-400 text-slate-900 hover:bg-lime-500 shadow-[0_2px_0_rgba(0,0,0,0.15)]"
                      : "bg-blue-700 text-white hover:bg-blue-800"
                  }`}
                >
                  Buy now <ArrowSquareOut size={12} weight="bold" />
                </button>
                <PaymentTrust />
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
          Payments are processed by our external partner. You'll be redirected in a new tab.
        </p>
      </div>
    </DashboardLayout>
  );
}
